/**
 * InfinitSales - Theme Context
 * Manages dark/light theme across the application
 */
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { applyBrandFont, getEmbedBranding } from '../utils/embed';

// Theme colors
export const themes = {
  light: {
    // Backgrounds
    bgPrimary: '#ffffff',
    bgSecondary: '#f0f2f5',
    bgTertiary: '#e9edef',
    bgChat: '#efeae2',
    
    // Text
    textPrimary: '#111b21',
    textSecondary: '#667781',
    textTertiary: '#8696a0',
    
    // Borders
    borderColor: '#e9edef',
    borderLight: '#f0f2f5',
    
    // Accent
    accentPrimary: '#00a884',
    accentSecondary: '#25d366',
    accentHover: '#008f72',
    
    // Messages
    messageSent: '#d9fdd3',
    messageReceived: '#ffffff',
    messageSentText: '#111b21',
    messageReceivedText: '#111b21',
    messageTimeSent: 'rgba(0, 0, 0, 0.45)',
    
    // Input
    inputBg: '#ffffff',
    inputBorder: '#e9edef',
    
    // Header/Sidebar
    headerBg: '#f0f2f5',
    sidebarBg: '#ffffff',
    
    // Status colors
    danger: '#ea4335',
    dangerHover: '#d33426',
    
    // Shadows
    shadowSm: '0 1px 2px rgba(0, 0, 0, 0.1)',
    shadowMd: '0 2px 8px rgba(0, 0, 0, 0.15)',
    shadowLg: '0 4px 16px rgba(0, 0, 0, 0.2)',
  },
  dark: {
    // Backgrounds
    bgPrimary: '#111b21',
    bgSecondary: '#202c33',
    bgTertiary: '#2a3942',
    bgChat: '#0b141a',
    
    // Text
    textPrimary: '#e9edef',
    textSecondary: '#8696a0',
    textTertiary: '#667781',
    
    // Borders
    borderColor: '#2a3942',
    borderLight: '#222d34',
    
    // Accent
    accentPrimary: '#00a884',
    accentSecondary: '#25d366',
    accentHover: '#00c49a',
    
    // Messages
    messageSent: '#005c4b',
    messageReceived: '#202c33',
    messageSentText: '#e9edef',
    messageReceivedText: '#e9edef',
    messageTimeSent: 'rgba(255, 255, 255, 0.6)',
    
    // Input
    inputBg: '#2a3942',
    inputBorder: '#3b4a54',
    
    // Header/Sidebar
    headerBg: '#202c33',
    sidebarBg: '#111b21',
    
    // Status colors
    danger: '#ea4335',
    dangerHover: '#d33426',
    
    // Shadows
    shadowSm: '0 1px 2px rgba(0, 0, 0, 0.3)',
    shadowMd: '0 2px 8px rgba(0, 0, 0, 0.4)',
    shadowLg: '0 4px 16px rgba(0, 0, 0, 0.5)',
  }
};

const ThemeContext = createContext();

// Mistura linear de duas cores #rrggbb (t = 0 devolve a, t = 1 devolve b).
function mix(a, b, t) {
  const pa = [1, 3, 5].map((i) => parseInt(a.slice(i, i + 2), 16));
  const pb = [1, 3, 5].map((i) => parseInt(b.slice(i, i + 2), 16));
  return `#${pa.map((v, i) => Math.round(v + (pb[i] - v) * t).toString(16).padStart(2, '0')).join('')}`;
}

/**
 * CRM embutido em painel parceiro (white label, 2026-10-22): a marca do
 * WORKSPACE (cores e fonte do campo `white_label`, entregue pelo painel)
 * por cima do tema claro. Só a paleta muda; telas e regras são as mesmas.
 */
export function brandTheme(base, marca) {
  if (!marca) return base;
  const accent = marca.accent || base.accentPrimary;
  const paper = marca.paper || base.bgSecondary;
  const ink = marca.ink || base.textPrimary;
  return {
    ...base,
    bgSecondary: paper,
    headerBg: paper,
    bgTertiary: mix(paper, ink, 0.06),
    bgChat: mix(paper, ink, 0.03),
    textPrimary: ink,
    textSecondary: mix(ink, paper, 0.4),
    textTertiary: mix(ink, paper, 0.55),
    borderColor: mix(paper, ink, 0.12),
    borderLight: mix(paper, ink, 0.06),
    inputBorder: mix(paper, ink, 0.12),
    accentPrimary: accent,
    accentSecondary: accent,
    accentHover: marca.accent_hover || mix(accent, '#000000', 0.15),
    messageSent: mix(accent, '#ffffff', 0.86),
    messageSentText: ink,
    messageReceivedText: ink,
  };
}

export const ThemeProvider = ({ children }) => {
  // Lida uma vez por carga: a ponte /embed grava a marca e recarrega a aba.
  const [marca] = useState(() => getEmbedBranding());
  const [theme, setTheme] = useState(() => {
    if (marca) return 'light';
    // Migration: check new key first, fall back to old key for existing users
    const saved = localStorage.getItem('infinitsales-theme') || localStorage.getItem('lumensales-theme');
    if (saved) return saved;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    // No modo embutido o tema é o do painel: não grava a preferência da aba.
    if (!marca) localStorage.setItem('infinitsales-theme', theme);
    // Mantem a status bar do PWA (iOS 15+/Android) na cor do header do tema atual.
    // O SO escolhe o texto legivel (claro no tema escuro, escuro no tema claro),
    // evitando texto branco invisivel no modo claro.
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', themes[theme].bgPrimary);
  }, [theme, marca]);

  useEffect(() => {
    if (marca?.font) applyBrandFont(marca.font);
  }, [marca]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  // Objeto estável: muita tela tem `colors` nas dependências de efeito.
  const colors = useMemo(
    () => (marca ? brandTheme(themes.light, marca) : themes[theme]),
    [marca, theme],
  );

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, colors }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;